import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useVerificationWizard } from '../useVerificationWizard';
import OnboardingForm from '../../wizard/forms/OnboardingForm';
import LookupMultiSelect, { type LookupOption } from '../../shared/LookupMultiSelect';
import VerificationStepActionBar from '../VerificationStepActionBar';
import { VERIFICATION_STEPS } from '../../../types/verification';
import { validateOnboarding, type ValidationErrors } from '../../../types/wizard';
import { clinicApi } from '../../../services/clinicApi';
import { usePermissions, ClinicMgmtPermissions } from '@shared/context/usePermissions';
import '../../../pages/VerificationWizardPage.css';

const STEP = VERIFICATION_STEPS.CAPABILITIES;

const VerifyCapabilitiesStep: React.FC = () => {
  const {
    state,
    stepErrors,
    updateOnboarding,
    updateCategoryTypeIds,
    updateCapabilityTypeIds,
    saveCategories,
    saveCapabilityList,
    saveOnboarding,
    markStepConfirmed,
    markStepUpdated,
    markStepSkipped,
    getStepStatus,
    isSaving,
    setSaveError,
    beginSaving,
    endSaving,
  } = useVerificationWizard();

  const [isSavingStep, setIsSavingStep] = useState(false);
  // Onboarding rules (DOT count required and 1..999) are checked here before the
  // saves run; the creation wizard does the same on Next (SLV2-3210).
  const [onboardingErrors, setOnboardingErrors] = useState<ValidationErrors>({});
  const errors = { ...(stepErrors[STEP] || {}), ...onboardingErrors };

  // The onboarding endpoint deliberately pins appointment method, reporting
  // method, and W-9 to their current values for users without profile-edit
  // rights (SLV2-2999 boundary) — so the UI must not offer those fields to
  // roles that cannot save them (SLV2-3155).
  const { hasPermission } = usePermissions();
  const canEditProfileFields = hasPermission(ClinicMgmtPermissions.EditClinicProfile);

  const categoriesQuery = useQuery({
    queryKey: ['categoryTypes'],
    queryFn: () => clinicApi.getCategoryTypes(),
  });
  const capabilitiesQuery = useQuery({
    queryKey: ['capabilityTypes'],
    queryFn: () => clinicApi.getCapabilityTypes(),
  });

  const categoryOptions: LookupOption[] = useMemo(
    () =>
      (categoriesQuery.data ?? [])
        .filter((c) => c.isActive)
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((c) => ({ id: c.id, name: c.name })),
    [categoriesQuery.data]
  );

  const capabilityOptions: LookupOption[] = useMemo(
    () =>
      (capabilitiesQuery.data ?? [])
        .filter((c) => c.isActive)
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((c) => ({ id: c.id, name: c.name })),
    [capabilitiesQuery.data]
  );

  // SLV2-3155: each save resolves false on failure instead of throwing, so one
  // failing section (e.g. a 403 on categories for a user without that role)
  // no longer silently aborts the rest — every section is attempted, and the
  // combined outcome is reported.
  //
  // beginSaving/endSaving hold the context's ref-counted isSaving for the
  // whole sequence so the wizard's Next/Back/Cancel stay disabled between the
  // awaited calls (isSavingStep covers this step's own action bar). The step
  // is marked updated only when EVERY section saved — a partial failure
  // leaves it honestly pending.
  const handleSave = async () => {
    const validation = validateOnboarding(state.onboarding, true);
    setOnboardingErrors(validation);
    if (Object.keys(validation).length > 0) {
      setSaveError('Please correct the highlighted onboarding fields before saving.');
      return;
    }
    setSaveError(null);
    setIsSavingStep(true);
    beginSaving();
    try {
      const failed: string[] = [];
      if (!(await saveCategories())) failed.push('categories');
      if (!(await saveCapabilityList())) failed.push('capability list');
      if (!(await saveOnboarding())) failed.push('onboarding information');

      if (failed.length > 0) {
        setSaveError(
          `Some sections failed to save: ${failed.join(', ')}. ` +
          'The remaining sections were saved. Check your permissions or try again.'
        );
      } else {
        markStepUpdated(STEP);
      }
    } finally {
      endSaving();
      setIsSavingStep(false);
    }
  };

  return (
    <div className="wizard-step">
      <h2>Verify Capabilities &amp; Onboarding</h2>
      <p className="step-description">Review the clinic's categories, capabilities, and onboarding information.</p>

      {/* The Emergency Room / After Hours / PAT / X-Ray tiles and fit-test dropdowns
          were removed (Clinic Network decision 2026-09-16). Each attribute has one
          home in the lists below; the server derives the legacy flag columns. */}
      <div className="verify-section">
        <h3 className="verify-section-heading">Categories</h3>
        <LookupMultiSelect
          options={categoryOptions}
          selectedIds={state.categoryTypeIds}
          onChange={updateCategoryTypeIds}
          itemNoun="category"
          itemNounPlural="categories"
          isLoading={categoriesQuery.isLoading}
          loadError={categoriesQuery.isError ? 'Failed to load categories.' : null}
          onRetry={() => categoriesQuery.refetch()}
        />
      </div>

      <div className="verify-section-divider">
        <h3 className="verify-section-heading">Capabilities</h3>
        <LookupMultiSelect
          options={capabilityOptions}
          selectedIds={state.capabilityTypeIds}
          onChange={updateCapabilityTypeIds}
          itemNoun="capability"
          itemNounPlural="capabilities"
          isLoading={capabilitiesQuery.isLoading}
          loadError={capabilitiesQuery.isError ? 'Failed to load capabilities.' : null}
          onRetry={() => capabilitiesQuery.refetch()}
        />
      </div>

      <div className="verify-section-divider">
        <h3 className="verify-section-heading">Onboarding Information</h3>
        <OnboardingForm
          values={state.onboarding}
          onChange={updateOnboarding}
          errors={errors}
          showProfileOwnedFields={canEditProfileFields}
        />
      </div>

      <VerificationStepActionBar
        stepStatus={getStepStatus(STEP)}
        isSaving={isSaving || isSavingStep}
        onSaveChanges={handleSave}
        onNoChanges={() => markStepConfirmed(STEP)}
        onSkip={(notes) => markStepSkipped(STEP, notes)}
      />
    </div>
  );
};

export default VerifyCapabilitiesStep;
